import { Helmet } from "react-helmet-async";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { CookieConsent } from "@/components/CookieConsent";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Heart, 
  Shield, 
  Clock, 
  Smile, 
  MessageCircle, 
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  ArrowRight,
  Quote,
  Award,
  Users,
  Star
} from "lucide-react";

// Import images
import patientSmileImg from "@/assets/patient-smile.jpg";
import healthyGumsImg from "@/assets/healthy-gums.jpg";
import dentistConsultationImg from "@/assets/dentist-consultation.jpg";

const translations = {
  de: {
    title: "Für Patienten",
    subtitle: "Was Sie über moderne Zahnimplantate wissen sollten",
    heroText: "Sachlich erklärt – Herstellerinformation",
    
    introTitle: "Warum ist die Wahl des richtigen Aufbaus so wichtig?",
    introText: "Bei einem Zahnimplantat wird ein Verbindungsstück (Abutment) zwischen dem Implantat im Knochen und der sichtbaren Krone benötigt. Die Gestaltung dieses Verbindungsstücks ist ein wichtiger Bestandteil der prothetischen Planung.",
    
    problemsTitle: "Was die Forschung zeigt",
    problems: [
      {
        icon: "alert",
        title: "Zementreste sind ein untersuchtes Thema",
        text: "In einer klinischen Untersuchung (Wilson 2009) wurden an 81 % der untersuchten Stellen mit Anzeichen einer periimplantären Erkrankung Zementreste gefunden. Das beschreibt eine Beobachtung und Assoziation, keinen Kausalitätsnachweis."
      },
      {
        icon: "alert",
        title: "Wiederholte Abutment-Wechsel werden untersucht",
        text: "Jedes Mal, wenn das Verbindungsstück gewechselt wird, muss sich das Zahnfleisch neu anpassen. In einer Meta-Analyse zeigte sich bei definitiven Abutments ein um 0,19 mm geringerer marginaler Knochenverlust – ein Studienmittelwert, keine Aussage über den Einzelfall."
      },
      {
        icon: "alert",
        title: "Standardlösungen passen nicht immer",
        text: "Jeder Mund ist anders. Vorgefertigte Verbindungsstücke berücksichtigen nicht die individuelle Form Ihres Zahnfleisches."
      }
    ],
    
    solutionTitle: "Die EAP® Lösung",
    solutionSubtitle: "Konstruktionsprinzipien sachlich erklärt",
    benefits: [
      {
        icon: "heart",
        title: "Titan im Zahnfleischbereich",
        text: "Nach Herstellerangaben liegt beim EAP®-Konzept Titan im Zahnfleischbereich, und die Geometrie wird patientenspezifisch gestaltet. Ziel der Konstruktion ist es, Materialübergänge möglichst weit oben zu positionieren. Eine konkrete Risikoreduktion lässt sich daraus ohne klinische Vergleichsdaten nicht ableiten."
      },
      {
        icon: "smile",
        title: "Natürliches Aussehen",
        text: "Individuell an Ihre Zahnfleischform angepasst – für ein ästhetisches Ergebnis ohne sichtbare Ränder oder graue Schimmer."
      },
      {
        icon: "clock",
        title: "Auf eine dauerhafte Versorgung ausgelegt",
        text: "Das EAP® Abutment ist als definitives Abutment vorgesehen und soll nicht wiederholt gewechselt werden. Die tatsächliche Langzeitentwicklung hängt von vielen individuellen Faktoren ab."
      },
      {
        icon: "shield",
        title: "Wissenschaftlicher Hintergrund",
        text: "Die Konstruktionsprinzipien orientieren sich an allgemeiner Fachliteratur (über 120 Publikationen, mehr als 15 Jahre Entwicklung). Diese Literatur ist kein produktspezifischer klinischer Nachweis für EAP®. In über 45 Ländern patentiert."
      }
    ],
    
    researchTitle: "Was Wissenschaftler herausgefunden haben",
    researchItems: [
      "In einer Meta-Analyse zeigten definitive Abutments im Mittel einen geringeren marginalen Knochenverlust (Studienmittelwert, keine Aussage zum Einzelfall)",
      "Individuelle Geometrien werden in der Literatur im Zusammenhang mit Emergenzprofil und Weichgewebe untersucht",
      "Zementreste im Weichgewebe werden in Untersuchungen mit periimplantären Befunden in Verbindung gebracht",
      "Titanoberflächen und Zellanhaftung sind Gegenstand von In-vitro-Untersuchungen"
    ],
    
    ctaTitle: "Sprechen Sie Ihren Zahnarzt an",
    ctaText: "Fragen Sie bei Ihrem nächsten Zahnarztbesuch nach EAP® Abutments. Ihr Zahnarzt kann Ihnen erklären, ob diese Lösung für Sie geeignet ist.",
    ctaButton: "Die 3. Meinung ansehen",
    ctaSecondary: "Wissenschaftliche Hintergründe",
    
    questionTitle: "Fragen, die Sie stellen können",
    questions: [
      "Verwenden Sie individuelle oder vorgefertigte Abutments?",
      "Wie vermeiden Sie Zementprobleme bei meinem Implantat?",
      "Wird das Abutment dauerhaft eingesetzt oder später gewechselt?",
      "Kennen Sie die EAP® Technologie?"
    ],
    
    disclaimerTitle: "Hinweis",
    disclaimerText: "Diese Informationen dienen der allgemeinen Aufklärung und ersetzen nicht das Gespräch mit Ihrem Zahnarzt. Die individuell beste Lösung hängt von Ihrer persönlichen Situation ab."
  },
  en: {
    title: "For Patients",
    subtitle: "What you should know about modern dental implants",
    heroText: "Explained plainly – manufacturer information",
    
    introTitle: "Why does choosing the right connector matter?",
    introText: "With a dental implant, a connecting piece (abutment) is needed between the implant in the bone and the visible crown. The design of this connector is an important part of the prosthetic planning.",
    
    problemsTitle: "What research shows",
    problems: [
      {
        icon: "alert",
        title: "Residual cement is a studied topic",
        text: "In one clinical study (Wilson 2009), residual cement was found at 81% of the examined test sites showing signs of peri-implant disease. This is an observation and association, not proof of causation."
      },
      {
        icon: "alert",
        title: "Repeated abutment changes are studied",
        text: "Each time the connector is changed, the soft tissue has to readapt. A meta-analysis reported 0.19 mm less marginal bone loss with definitive abutments – a study average, not a prediction for an individual case."
      },
      {
        icon: "alert",
        title: "Standard solutions don't always fit",
        text: "Every mouth is different. Prefabricated connectors don't consider your individual gum shape."
      }
    ],
    
    solutionTitle: "The EAP® Solution",
    solutionSubtitle: "Design principles explained factually",
    benefits: [
      {
        icon: "heart",
        title: "Titanium at the gingival level",
        text: "According to manufacturer information, titanium is positioned in the gingival region and the geometry is made patient-specific. The design aims to place material transitions as coronally as possible. Without clinical comparative data, no specific risk reduction can be derived from this."
      },
      {
        icon: "smile",
        title: "Natural appearance",
        text: "Individually adapted to your gum shape – for an aesthetic result without visible edges or gray shimmer."
      },
      {
        icon: "clock",
        title: "Designed as a definitive restoration",
        text: "The EAP® abutment is intended as a definitive abutment and is not meant to be exchanged repeatedly. Actual long-term development depends on many individual factors."
      },
      {
        icon: "shield",
        title: "Scientific background",
        text: "The design principles draw on general scientific literature (over 120 publications, more than 15 years of development). This literature is not product-specific clinical evidence for EAP®. Patented in over 45 countries."
      }
    ],
    
    researchTitle: "What scientists have discovered",
    researchItems: [
      "A meta-analysis found a lower mean marginal bone loss for definitive abutments (study average, not a prediction for individual cases)",
      "Individual geometries are studied in the literature in relation to emergence profile and soft tissue",
      "Residual cement in soft tissue is associated with peri-implant findings in clinical studies",
      "Titanium surfaces and cell attachment are the subject of in-vitro research"
    ],
    
    ctaTitle: "Talk to your dentist",
    ctaText: "Ask about EAP® abutments at your next dental visit. Your dentist can explain if this solution is right for you.",
    ctaButton: "View the Third Opinion",
    ctaSecondary: "Scientific background",
    
    questionTitle: "Questions you can ask",
    questions: [
      "Do you use individual or prefabricated abutments?",
      "How do you avoid cement problems with my implant?",
      "Will the abutment be placed permanently or changed later?",
      "Are you familiar with EAP® technology?"
    ],
    
    disclaimerTitle: "Note",
    disclaimerText: "This information is for general education and does not replace consultation with your dentist. The best individual solution depends on your personal situation."
  },
  fr: {
    title: "Pour les patients",
    subtitle: "Ce que vous devez savoir sur les implants dentaires modernes",
    heroText: "Expliqué simplement – information du fabricant",
    
    introTitle: "Pourquoi le choix du bon connecteur est-il important ?",
    introText: "Avec un implant dentaire, une pièce de connexion (pilier) est nécessaire entre l'implant dans l'os et la couronne visible. La conception de ce connecteur est un élément important de la planification prothétique.",
    
    problemsTitle: "Ce que montre la recherche",
    problems: [
      {
        icon: "alert",
        title: "Les résidus de ciment sont un sujet étudié",
        text: "Dans une étude clinique (Wilson 2009), des résidus de ciment ont été trouvés sur 81 % des sites examinés présentant des signes de maladie péri-implantaire. Il s'agit d'une association, non d'une preuve de causalité."
      },
      {
        icon: "alert",
        title: "Les changements répétés de pilier sont étudiés",
        text: "Chaque fois que le connecteur est changé, la gencive doit se réadapter. Une méta-analyse rapporte 0,19 mm de perte osseuse marginale en moins avec des piliers définitifs – une moyenne d'étude, pas une prédiction individuelle."
      },
      {
        icon: "alert",
        title: "Les solutions standard ne conviennent pas toujours",
        text: "Chaque bouche est différente. Les connecteurs préfabriqués ne tiennent pas compte de la forme individuelle de votre gencive."
      }
    ],
    
    solutionTitle: "La solution EAP®",
    solutionSubtitle: "Principes de conception expliqués objectivement",
    benefits: [
      {
        icon: "heart",
        title: "Titane au niveau gingival",
        text: "Selon les informations du fabricant, le titane se situe dans la zone gingivale et la géométrie est patient-spécifique. La conception vise à positionner les transitions de matériaux le plus coronairement possible. Sans données cliniques comparatives, aucune réduction de risque ne peut en être déduite."
      },
      {
        icon: "smile",
        title: "Apparence naturelle",
        text: "Adapté individuellement à la forme de votre gencive – pour un résultat esthétique sans bords visibles ni reflets gris."
      },
      {
        icon: "clock",
        title: "Conçu comme restauration définitive",
        text: "Le pilier EAP® est prévu comme pilier définitif et n'est pas destiné à être changé de façon répétée. L'évolution à long terme dépend de nombreux facteurs individuels."
      },
      {
        icon: "shield",
        title: "Contexte scientifique",
        text: "Les principes de conception s'appuient sur la littérature scientifique générale (plus de 120 publications, plus de 15 ans de développement). Cette littérature n'est pas une preuve clinique spécifique au produit EAP®. Breveté dans plus de 45 pays."
      }
    ],
    
    researchTitle: "Ce que les scientifiques ont découvert",
    researchItems: [
      "Une méta-analyse a montré une perte osseuse marginale moyenne plus faible pour les piliers définitifs (moyenne d'étude, sans valeur prédictive individuelle)",
      "Les géométries individuelles sont étudiées en lien avec le profil d'émergence et les tissus mous",
      "Les résidus de ciment sont associés à des constats péri-implantaires dans des études",
      "Les surfaces en titane et l'adhésion cellulaire font l'objet de recherches in vitro"
    ],
    
    ctaTitle: "Parlez à votre dentiste",
    ctaText: "Demandez les piliers EAP® lors de votre prochaine visite dentaire. Votre dentiste peut vous expliquer si cette solution vous convient.",
    ctaButton: "Voir la 3e opinion",
    ctaSecondary: "Contexte scientifique",
    
    questionTitle: "Questions que vous pouvez poser",
    questions: [
      "Utilisez-vous des piliers individuels ou préfabriqués ?",
      "Comment évitez-vous les problèmes de ciment avec mon implant ?",
      "Le pilier sera-t-il placé de façon permanente ou changé plus tard ?",
      "Connaissez-vous la technologie EAP® ?"
    ],
    
    disclaimerTitle: "Remarque",
    disclaimerText: "Ces informations sont à titre éducatif général et ne remplacent pas la consultation avec votre dentiste. La meilleure solution individuelle dépend de votre situation personnelle."
  },
  nl: {
    title: "Voor patiënten",
    subtitle: "Wat u moet weten over moderne tandimplantaten",
    heroText: "Eenvoudig uitgelegd – informatie van de fabrikant",
    
    introTitle: "Waarom is de keuze van de juiste connector belangrijk?",
    introText: "Bij een tandimplantaat is een verbindingsstuk (abutment) nodig tussen het implantaat in het bot en de zichtbare kroon. Het ontwerp van deze connector is een belangrijk onderdeel van de prothetische planning.",
    
    problemsTitle: "Wat onderzoek aantoont",
    problems: [
      {
        icon: "alert",
        title: "Cementresten zijn een onderzocht onderwerp",
        text: "In één klinisch onderzoek (Wilson 2009) werd cementresidu gevonden op 81 % van de onderzochte plaatsen met tekenen van peri-implantaire ziekte. Dit is een associatie, geen bewijs van causaliteit."
      },
      {
        icon: "alert",
        title: "Herhaalde abutmentwissels worden onderzocht",
        text: "Elke keer dat de connector wordt gewisseld, moet het weefsel zich opnieuw aanpassen. Een meta-analyse rapporteerde 0,19 mm minder marginaal botverlies bij definitieve abutments – een studiegemiddelde, geen voorspelling voor het individuele geval."
      },
      {
        icon: "alert",
        title: "Standaardoplossingen passen niet altijd",
        text: "Elke mond is anders. Geprefabriceerde connectoren houden geen rekening met uw individuele tandvleesvorm."
      }
    ],
    
    solutionTitle: "De EAP® oplossing",
    solutionSubtitle: "Ontwerpprincipes feitelijk uitgelegd",
    benefits: [
      {
        icon: "heart",
        title: "Titanium op tandvleesniveau",
        text: "Volgens informatie van de fabrikant bevindt titanium zich in het tandvleesgebied en is de geometrie patiëntspecifiek. Het ontwerp beoogt materiaalovergangen zo coronaal mogelijk te plaatsen. Zonder klinische vergelijkende gegevens kan hieruit geen risicoreductie worden afgeleid."
      },
      {
        icon: "smile",
        title: "Natuurlijk uiterlijk",
        text: "Individueel aangepast aan uw tandvleesvorm – voor een esthetisch resultaat zonder zichtbare randen of grijze glans."
      },
      {
        icon: "clock",
        title: "Bedoeld als definitieve voorziening",
        text: "Het EAP® abutment is bedoeld als definitief abutment en niet om herhaald te wisselen. De werkelijke ontwikkeling op lange termijn hangt af van veel individuele factoren."
      },
      {
        icon: "shield",
        title: "Wetenschappelijke achtergrond",
        text: "De ontwerpprincipes baseren zich op algemene wetenschappelijke literatuur (meer dan 120 publicaties, meer dan 15 jaar ontwikkeling). Die literatuur is geen productspecifiek klinisch bewijs voor EAP®. Gepatenteerd in meer dan 45 landen."
      }
    ],
    
    researchTitle: "Wat wetenschappers hebben ontdekt",
    researchItems: [
      "Een meta-analyse toonde voor definitieve abutments gemiddeld een lager marginaal botverlies (studiemiddel, geen uitspraak over het individuele geval)",
      "Individuele geometrieën worden in de literatuur onderzocht in relatie tot emergentieprofiel en weefsel",
      "Cementresten worden in onderzoeken geassocieerd met peri-implantaire bevindingen",
      "Titaniumoppervlakken en celhechting zijn onderwerp van in-vitro-onderzoek"
    ],
    
    ctaTitle: "Praat met uw tandarts",
    ctaText: "Vraag naar EAP® abutments bij uw volgende tandartsbezoek. Uw tandarts kan uitleggen of deze oplossing geschikt is voor u.",
    ctaButton: "Bekijk de 3e mening",
    ctaSecondary: "Wetenschappelijke achtergrond",
    
    questionTitle: "Vragen die u kunt stellen",
    questions: [
      "Gebruikt u individuele of geprefabriceerde abutments?",
      "Hoe vermijdt u cementproblemen bij mijn implantaat?",
      "Wordt het abutment permanent geplaatst of later gewisseld?",
      "Bent u bekend met de EAP® technologie?"
    ],
    
    disclaimerTitle: "Opmerking",
    disclaimerText: "Deze informatie is voor algemene educatie en vervangt niet het gesprek met uw tandarts. De beste individuele oplossing hangt af van uw persoonlijke situatie."
  },
  it: {
    title: "Per i pazienti",
    subtitle: "Cosa dovresti sapere sugli impianti dentali moderni",
    heroText: "Spiegato in modo semplice – informazione del produttore",
    
    introTitle: "Perché la scelta del connettore giusto è importante?",
    introText: "Con un impianto dentale, è necessario un pezzo di collegamento (abutment) tra l'impianto nell'osso e la corona visibile. La progettazione di questo connettore è una parte importante della pianificazione protesica.",
    
    problemsTitle: "Cosa mostra la ricerca",
    problems: [
      {
        icon: "alert",
        title: "I residui di cemento sono un tema studiato",
        text: "In uno studio clinico (Wilson 2009) sono stati trovati residui di cemento nell'81 % dei siti esaminati con segni di malattia perimplantare. Si tratta di un'associazione, non di una prova di causalità."
      },
      {
        icon: "alert",
        title: "I cambi ripetuti di abutment sono studiati",
        text: "Ogni volta che il connettore viene cambiato, il tessuto deve riadattarsi. Una meta-analisi ha riportato 0,19 mm di perdita ossea marginale in meno con abutment definitivi – una media di studio, non una previsione individuale."
      },
      {
        icon: "alert",
        title: "Le soluzioni standard non sempre si adattano",
        text: "Ogni bocca è diversa. I connettori prefabbricati non considerano la forma individuale della tua gengiva."
      }
    ],
    
    solutionTitle: "La soluzione EAP®",
    solutionSubtitle: "Principi costruttivi spiegati in modo oggettivo",
    benefits: [
      {
        icon: "heart",
        title: "Titanio a livello gengivale",
        text: "Secondo le informazioni del produttore, il titanio si trova nella zona gengivale e la geometria è specifica per il paziente. Il progetto mira a posizionare i passaggi di materiale il più coronalmente possibile. Senza dati clinici comparativi non se ne può dedurre una riduzione del rischio."
      },
      {
        icon: "smile",
        title: "Aspetto naturale",
        text: "Adattato individualmente alla forma della tua gengiva – per un risultato estetico senza bordi visibili o riflessi grigi."
      },
      {
        icon: "clock",
        title: "Concepito come restauro definitivo",
        text: "L'abutment EAP® è previsto come abutment definitivo e non è destinato a essere sostituito ripetutamente. L'evoluzione a lungo termine dipende da molti fattori individuali."
      },
      {
        icon: "shield",
        title: "Contesto scientifico",
        text: "I principi costruttivi si basano su letteratura scientifica generale (oltre 120 pubblicazioni, più di 15 anni di sviluppo). Questa letteratura non è una prova clinica specifica del prodotto EAP®. Brevettato in oltre 45 paesi."
      }
    ],
    
    researchTitle: "Cosa hanno scoperto gli scienziati",
    researchItems: [
      "Una meta-analisi ha mostrato una perdita ossea marginale media inferiore per gli abutment definitivi (media di studio, non una previsione per il singolo caso)",
      "Le geometrie individuali sono studiate in relazione al profilo di emergenza e ai tessuti molli",
      "I residui di cemento sono associati negli studi a riscontri perimplantari",
      "Le superfici in titanio e l'adesione cellulare sono oggetto di ricerca in vitro"
    ],
    
    ctaTitle: "Parla con il tuo dentista",
    ctaText: "Chiedi degli abutment EAP® alla tua prossima visita dentistica. Il tuo dentista può spiegarti se questa soluzione è giusta per te.",
    ctaButton: "Vedi la 3a opinione",
    ctaSecondary: "Contesto scientifico",
    
    questionTitle: "Domande che puoi fare",
    questions: [
      "Usate abutment individuali o prefabbricati?",
      "Come evitate i problemi di cemento con il mio impianto?",
      "L'abutment sarà posizionato permanentemente o cambiato in seguito?",
      "Conoscete la tecnologia EAP®?"
    ],
    
    disclaimerTitle: "Nota",
    disclaimerText: "Queste informazioni sono a scopo educativo generale e non sostituiscono la consultazione con il tuo dentista. La migliore soluzione individuale dipende dalla tua situazione personale."
  },
  es: {
    title: "Para pacientes",
    subtitle: "Lo que debe saber sobre los implantes dentales modernos",
    heroText: "Explicado de forma sencilla – información del fabricante",
    
    introTitle: "¿Por qué es importante elegir el conector adecuado?",
    introText: "Con un implante dental, se necesita una pieza de conexión (pilar) entre el implante en el hueso y la corona visible. El diseño de este conector es una parte importante de la planificación prostodóncica.",
    
    problemsTitle: "Lo que muestra la investigación",
    problems: [
      {
        icon: "alert",
        title: "Los restos de cemento son un tema estudiado",
        text: "En un estudio clínico (Wilson 2009) se encontraron restos de cemento en el 81 % de los sitios examinados con signos de enfermedad periimplantaria. Es una asociación, no una prueba de causalidad."
      },
      {
        icon: "alert",
        title: "Los cambios repetidos de pilar están estudiados",
        text: "Cada vez que se cambia el conector, el tejido debe readaptarse. Un metaanálisis informó 0,19 mm menos de pérdida ósea marginal con pilares definitivos – un promedio de estudio, no una predicción individual."
      },
      {
        icon: "alert",
        title: "Las soluciones estándar no siempre se ajustan",
        text: "Cada boca es diferente. Los conectores prefabricados no consideran la forma individual de su encía."
      }
    ],
    
    solutionTitle: "La solución EAP®",
    solutionSubtitle: "Principios de diseño explicados objetivamente",
    benefits: [
      {
        icon: "heart",
        title: "Titanio a nivel gingival",
        text: "Según la información del fabricante, el titanio se encuentra en la zona gingival y la geometría es específica del paciente. El diseño busca situar las transiciones de material lo más coronalmente posible. Sin datos clínicos comparativos no puede derivarse una reducción del riesgo."
      },
      {
        icon: "smile",
        title: "Apariencia natural",
        text: "Adaptado individualmente a la forma de su encía – para un resultado estético sin bordes visibles ni brillo gris."
      },
      {
        icon: "clock",
        title: "Concebido como restauración definitiva",
        text: "El pilar EAP® está previsto como pilar definitivo y no para cambiarse repetidamente. La evolución real a largo plazo depende de muchos factores individuales."
      },
      {
        icon: "shield",
        title: "Contexto científico",
        text: "Los principios de diseño se basan en literatura científica general (más de 120 publicaciones, más de 15 años de desarrollo). Esa literatura no es evidencia clínica específica del producto EAP®. Patentado en más de 45 países."
      }
    ],
    
    researchTitle: "Lo que los científicos han descubierto",
    researchItems: [
      "Un metaanálisis mostró una pérdida ósea marginal media menor con pilares definitivos (promedio de estudio, no una predicción para el caso individual)",
      "Las geometrías individuales se estudian en relación con el perfil de emergencia y los tejidos blandos",
      "Los restos de cemento se asocian en estudios con hallazgos periimplantarios",
      "Las superficies de titanio y la adhesión celular son objeto de investigación in vitro"
    ],
    
    ctaTitle: "Hable con su dentista",
    ctaText: "Pregunte por los pilares EAP® en su próxima visita dental. Su dentista puede explicarle si esta solución es adecuada para usted.",
    ctaButton: "Ver la 3.ª opinión",
    ctaSecondary: "Contexto científico",
    
    questionTitle: "Preguntas que puede hacer",
    questions: [
      "¿Utilizan pilares individuales o prefabricados?",
      "¿Cómo evitan los problemas de cemento con mi implante?",
      "¿Se colocará el pilar permanentemente o se cambiará después?",
      "¿Conocen la tecnología EAP®?"
    ],
    
    disclaimerTitle: "Nota",
    disclaimerText: "Esta información es para educación general y no reemplaza la consulta con su dentista. La mejor solución individual depende de su situación personal."
  }
};

const PatientInfo = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "heart": return <Heart className="w-8 h-8 text-primary" />;
      case "shield": return <Shield className="w-8 h-8 text-primary" />;
      case "clock": return <Clock className="w-8 h-8 text-primary" />;
      case "smile": return <Smile className="w-8 h-8 text-primary" />;
      case "alert": return <AlertTriangle className="w-6 h-6 text-amber-500" />;
      default: return <CheckCircle2 className="w-8 h-8 text-primary" />;
    }
  };

  const seo: Record<string, { title: string; description: string }> = {
    de: { title: "Für Patienten – Moderne Zahnimplantate verständlich erklärt", description: "Was Sie als Patient über Implantat-Abutments wissen sollten: Ti-Base, individuelles Abutment, Emergenzprofil und Materialien – verständlich und sachlich erklärt." },
    en: { title: "For Patients – Modern Dental Implants Explained | EAP®", description: "What patients should know about implant abutments: ti-base, customized abutment, emergence profile and materials – explained plainly." },
    fr: { title: "Pour les patients – Implants dentaires modernes expliqués | EAP®", description: "Ce que les patients doivent savoir sur les piliers d'implants dentaires modernes – expliqué objectivement." },
    nl: { title: "Voor patiënten – Moderne tandimplantaten uitgelegd | EAP®", description: "Wat patiënten moeten weten over moderne implantaat-abutments – objectief en eenvoudig uitgelegd." },
    it: { title: "Per i pazienti – Impianti dentali moderni spiegati | EAP®", description: "Ciò che i pazienti dovrebbero sapere sugli abutment moderni per impianti dentali – spiegato in modo oggettivo." },
    es: { title: "Para pacientes – Implantes dentales modernos explicados | EAP®", description: "Lo que los pacientes deben saber sobre los pilares modernos para implantes dentales – explicado objetivamente." },
  };
  const meta = seo[language] || seo.en;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href="https://eap-abutments.com/fuer-patienten" />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content="https://eap-abutments.com/fuer-patienten" />
        <meta property="og:type" content="article" />
      </Helmet>
      <Navigation />
      
      
      <main className="pt-24 pb-16">
        {/* Hero Section - Emotional with Image */}
        <section className="relative overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img 
              src={patientSmileImg} 
              alt="Zufriedene Patientin mit strahlendem Lächeln"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/40" />
          </div>
          
          <div className="relative container mx-auto px-4 py-20 md:py-32">
            <div className="max-w-2xl">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-primary/20 text-primary text-sm font-medium mb-6 backdrop-blur-sm">
                <Sparkles className="w-4 h-4 mr-2" />
                {t.heroText}
              </span>
              <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
                {t.title}
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed mb-8">
                {t.subtitle}
              </p>
              
              {/* Trust Indicators */}
              <div className="flex flex-wrap gap-6 mt-8">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Award className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-bold text-foreground">50</div>
                    <div className="text-xs text-muted-foreground">{language === 'de' ? 'Studien' : 'Studies'}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-bold text-foreground">45+</div>
                    <div className="text-xs text-muted-foreground">{language === 'de' ? 'Länder' : 'Countries'}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Star className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-bold text-foreground">15+</div>
                    <div className="text-xs text-muted-foreground">{language === 'de' ? 'Jahre' : 'Years'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction - With Image */}
        <section className="container mx-auto px-4 py-16">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src={healthyGumsImg} 
                alt="Gesundes Zahnfleisch"
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 right-0 bg-primary text-primary-foreground p-4 rounded-xl shadow-lg hidden md:block lg:-right-6">
                <div className="text-3xl font-bold">81%</div>
                <div className="text-sm opacity-90">{language === 'de' ? 'Stellen mit Zementresten (Wilson 2009)' : 'sites with residual cement (Wilson 2009)'}</div>
              </div>
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                {t.introTitle}
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                {t.introText}
              </p>
              <div className="flex items-start gap-4 p-4 bg-primary/5 rounded-xl border border-primary/20">
                <Quote className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                <p className="text-muted-foreground italic">
                  {language === 'de' 
                    ? '"Das Abutment ist ein wichtiger Bestandteil der prothetischen Planung – Form, Material und Lage der Materialübergänge werden individuell festgelegt."'
                    : '"The abutment is an important part of prosthetic planning – shape, material and the position of material transitions are defined individually."'}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Problems Section - More Visual */}
        <section className="bg-gradient-to-b from-destructive/5 to-transparent py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-destructive/10 text-destructive text-sm font-medium mb-4">
                <AlertTriangle className="w-4 h-4 mr-2" />
                {language === 'de' ? 'Wichtig zu wissen' : 'Important to know'}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                {t.problemsTitle}
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {t.problems.map((problem, index) => (
                <Card key={index} className="bg-card border-destructive/20 hover:border-destructive/40 transition-all hover:shadow-lg group">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-2xl bg-destructive/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                      <AlertTriangle className="w-7 h-7 text-destructive" />
                    </div>
                    <h3 className="font-bold text-lg text-foreground mb-3">
                      {problem.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {problem.text}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Solution Section - With Image */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                {/* Image Side */}
                <div className="relative order-2 lg:order-1">
                  <img 
                    src={dentistConsultationImg} 
                    alt="Beratungsgespräch beim Zahnarzt"
                    className="rounded-2xl shadow-2xl w-full"
                  />
                  {/* Floating Cards */}
                  <div className="absolute -top-6 left-0 bg-primary text-primary-foreground p-4 rounded-xl shadow-lg hidden md:flex items-center gap-3 lg:-left-6">
                    <Shield className="w-6 h-6" />
                    <div>
                      <div className="font-bold">0,19 mm</div>
                      <div className="text-xs opacity-90">{language === 'de' ? 'weniger Knochenverlust (Studienmittelwert)' : 'less bone loss (study average)'}</div>
                    </div>
                  </div>
                  <div className="absolute -bottom-6 right-0 bg-card p-4 rounded-xl shadow-lg border border-primary/20 hidden md:flex items-center gap-3 lg:-right-6">
                    <Heart className="w-6 h-6 text-primary" />
                    <div>
                      <div className="font-bold text-foreground">{language === 'de' ? 'Definitives Abutment' : 'Definitive abutment'}</div>
                      <div className="text-xs text-muted-foreground">{language === 'de' ? 'One-Abutment-One-Time-Konzept' : 'One-abutment-one-time concept'}</div>
                    </div>
                  </div>
                </div>
                
                {/* Content Side */}
                <div className="order-1 lg:order-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                    <Sparkles className="w-4 h-4 mr-2" />
                    {language === 'de' ? 'Die Lösung' : 'The Solution'}
                  </span>
                  <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                    {t.solutionTitle}
                  </h2>
                  <p className="text-lg text-muted-foreground mb-8">
                    {t.solutionSubtitle}
                  </p>
                  
                  {/* Benefits List */}
                  <div className="space-y-4">
                    {t.benefits.map((benefit, index) => (
                      <div key={index} className="flex items-start gap-4 p-4 bg-primary/5 rounded-xl border border-primary/10 hover:border-primary/30 transition-colors">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                          {getIcon(benefit.icon)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground mb-1">
                            {benefit.title}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {benefit.text}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Research Summary - Visual Cards */}
        <section className="bg-gradient-to-b from-muted/30 to-transparent py-16">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {t.researchTitle}
              </h2>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {t.researchItems.map((item, index) => (
                <Card key={index} className="bg-card border-primary/20 hover:shadow-lg transition-all group">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                      <CheckCircle2 className="w-6 h-6 text-primary" />
                    </div>
                    <p className="text-foreground font-medium">{item}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Questions to Ask - Interactive Cards */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <span className="inline-flex items-center px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-sm font-medium mb-4">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  {language === 'de' ? 'Für Ihren nächsten Termin' : 'For your next appointment'}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                  {t.questionTitle}
                </h2>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {t.questions.map((question, index) => (
                  <Card key={index} className="bg-card border-primary/10 hover:border-primary/30 hover:shadow-md transition-all cursor-default">
                    <CardContent className="p-6 flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary font-bold">{index + 1}</span>
                      </div>
                      <p className="text-foreground italic leading-relaxed">"{question}"</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section - Emotional with Gradient */}
        <section className="py-20">
          <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/90 to-primary/80 rounded-3xl" />
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-0 w-72 h-72 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />
              </div>
              
              <div className="relative text-center p-6 sm:p-12 md:p-16">
                <Smile className="w-16 h-16 text-primary-foreground/80 mx-auto mb-6" />
                <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
                  {t.ctaTitle}
                </h2>
                <p className="text-lg text-primary-foreground/90 mb-10 max-w-2xl mx-auto">
                  {t.ctaText}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    size="lg"
                    className="bg-card text-primary hover:bg-card/90 shadow-lg font-semibold px-6 sm:px-8"
                    onClick={() => window.location.href = '/ai-press#third-opinion'}
                  >
                    {t.ctaButton}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <Button 
                    size="lg"
                    variant="outline"
                    className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
                    onClick={() => window.location.href = '/research'}
                  >
                    {t.ctaSecondary}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Dezenter interner Verweis auf „Die 3. Meinung" */}
        <section className="container mx-auto px-4 pt-8">
          <div className="max-w-3xl mx-auto text-center text-sm text-muted-foreground">
            <ul className="space-y-2">
              <li>
                <a
                  href="/ai-press#third-opinion"
                  className="font-medium text-primary underline underline-offset-4"
                >
                  Patienteninformation: Die 3. Meinung – 20 Fragen zu Implantat-Abutments
                </a>
              </li>
              <li>
                <a
                  href="/ti-base-oder-individuelles-abutment"
                  className="font-medium text-primary underline underline-offset-4"
                >
                  {language === 'de'
                    ? 'Leitfaden: Ti-Base oder individuelles Abutment?'
                    : 'Guide: ti-base or custom abutment?'}
                </a>
              </li>
            </ul>
          </div>
        </section>

        {/* Disclaimer */}

        <section className="container mx-auto px-4 py-8">
          <div className="max-w-3xl mx-auto">
            <Card className="bg-muted/30 border-muted">
              <CardContent className="p-6">
                <h3 className="font-semibold text-muted-foreground mb-2">
                  {t.disclaimerTitle}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t.disclaimerText}
                </p>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>

      <Footer />
      <CookieConsent />
    </div>
  );
};

export default PatientInfo;
